import React, { useEffect, useState } from "react";
import useAuthStore from "../../store/authStore";
import { Button } from "../catalyst/button";
import { Description, Field, Label } from "../catalyst/fieldset";
import { Busy } from "../Busy";
import { InputOtp } from "primereact/inputotp";

const VerifyEmail: React.FC = () => {
  const [otp, setOtp] = useState<string>("");
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [showMessages, setShowMessages] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [resendEnabled, setResendEnabled] = useState(false);

  const {
    currentUser,
    sendOtp,
    verifyOtp,
    isBusyAuthenticating,
    error,
    successMessage,
  } = useAuthStore();

  useEffect(() => {
    if (otpSent && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    } else if (timeLeft <= 0) {
      setResendEnabled(true);
    }
  }, [otpSent, timeLeft]);

  const handleSendOtp = async () => {
    setOtpSent(true);
    setShowMessages(true);
    setResendEnabled(false);
    setTimeLeft(60); // Reset the timer
    await sendOtp(currentUser?.email || "");
  };

  const handleVerifyOtp = async () => {
    await verifyOtp(currentUser?.email || "", otp);
  };

  const handleCancel = () => {
    setOtpSent(false);
    setOtp("");
    setShowMessages(false);
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  if (isBusyAuthenticating) {
    return <Busy />;
  }

  if (currentUser?.isEmailVerified) {
    return null;
  }

  return (
    <div className="bg-red-100 dark:bg-gray-800 border border-red-400 dark:border-gray-600 text-red-700 dark:text-gray-300 max-w-[800px] mx-auto px-4 py-6 rounded relative mb-5 duration-300">
      <Field>
        <Label className="dark:text-primary-Default">Email Verification</Label>
        <Description className="dark:text-gray-400">
          Your email address is not verified. Please verify your email address
          to continue using the application.
        </Description>

        <div className="mt-3">
          {!otpSent ? (
            <div className="w-full flex flex-row-reverse">
              <Button
                className="w-[120px] bg-red-400 dark:bg-red-600 text-white rounded h-8"
                outline
                onClick={handleSendOtp}
              >
                Verify Email
              </Button>
            </div>
          ) : (
            <>
              <p className="text-gray-700 dark:text-gray-300 transition-opacity duration-300">
                A verification code has been sent to:{" "}
                <strong>{currentUser?.email}</strong>
              </p>

              {showMessages && successMessage && (
                <p className="text-gray-600 dark:text-gray-400">
                  {successMessage}
                </p>
              )}
              <div className="flex flex-col sm:flex-row items-center space-x-2 mt-4 gap-3">
                <InputOtp
                  value={otp}
                  onChange={(e) => {
                    if (e.value) {
                      setOtp(e.value.toString());
                    }
                  }}
                  className="dark:text-white"
                  length={6}
                  integerOnly
                />

                <div className="mt-4 sm:mt-0">
                  {!resendEnabled ? (
                    <span className="text-sm text-gray-600 dark:text-gray-400 max-w-[100px]">
                      Resend in ({minutes}:{" "}
                      {seconds < 10 ? `0${seconds}` : seconds})
                    </span>
                  ) : (
                    <Button
                      color="emerald"
                      className="h-8 w-[160px]"
                      onClick={handleSendOtp}
                    >
                      Resend OTP
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {otpSent && (
            <div className="space-x-2 mt-4 duration-300 text-right">
              {showMessages && error && (
                <p className="text-center text-red-500 dark:text-red-400">
                  {error}
                </p>
              )}

              <Button
                className="bg-secondary-Default dark:bg-gray-600 dark:hover:bg-primary-Default hover:bg-gray-700 h-8 w-[100px] "
                outline
                onClick={handleCancel}
              >
                Cancel
              </Button>

              <Button
                color="emerald"
                className="text-white rounded h-8 w-[150px]"
                onClick={handleVerifyOtp}
                disabled={otp.length !== 6}
              >
                Verify
              </Button>
            </div>
          )}
        </div>
      </Field>
    </div>
  );
};

export default VerifyEmail;
